import './styles.css'

import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3CenterLeftIcon,
  Bars3Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline'
import type { Extensions } from '@tiptap/core'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FontSize } from './fontSizeExtension'
import { Placeholder } from './placeholderNode'

interface Props {
  value: string
  onChange: (html: string) => void
  placeholders?: string[]
  error?: string
  minHeight?: number
  className?: string
  ariaLabel?: string
  /**
   * 'block' (default) keeps paragraphs, headings, alignment and rules.
   * 'inline' hides the controls whose markup cannot survive being flattened
   * into an inline slot (see `htmlToInlineHtml`), so the toolbar never offers
   * formatting the destination will silently drop.
   */
  variant?: 'block' | 'inline'
  editable?: boolean
  /** Extra extensions appended to the built-in set (e.g. the mention node). */
  extensions?: Extensions
  /**
   * Intercept a keypress before ProseMirror sees it. Return true to swallow it.
   * A typeahead needs this for Enter / Tab / arrows, which the editor would
   * otherwise consume as a newline or an indent.
   */
  handleKeyDown?: (event: KeyboardEvent) => boolean
  /** Hands the editor instance out so a caller can drive it (insert nodes, read selection). */
  onEditorReady?: (editor: Editor | null) => void
}

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']
const FONT_FAMILIES: Array<{ label: string; value: string }> = [
  { label: 'Default', value: '' },
  { label: 'Times New Roman', value: 'Times New Roman, Times, serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Courier New', value: 'Courier New, Courier, monospace' },
  { label: 'Georgia', value: 'Georgia, serif' },
]
const COLORS = [
  '#111827',
  '#374151',
  '#6B7280',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#06B6D4',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#FFFFFF',
]

const ToolbarButton = ({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active?: boolean
  disabled?: boolean
  title: string
  onClick: () => void
  children: React.ReactNode
}) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={`px-2 py-1 rounded text-sm border transition-colors
      ${active ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-200 hover:bg-gray-100'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
)

const Divider = () => <span className="mx-1 w-px h-5 bg-gray-200 inline-block" />

export const RichTextEditor = ({
  value,
  onChange,
  placeholders = [],
  error,
  minHeight = 240,
  className = '',
  ariaLabel,
  variant = 'block',
  editable = true,
  extensions = [],
  handleKeyDown,
  onEditorReady,
}: Props) => {
  const [, force] = useState(0)
  const lastEmittedRef = useRef<string>('')
  // Held in a ref: `useEditor` captures editorProps once, but the typeahead's
  // handler closes over state that changes on every keystroke.
  const handleKeyDownRef = useRef(handleKeyDown)
  handleKeyDownRef.current = handleKeyDown

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer nofollow' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Placeholder,
      ...extensions,
    ],
    editorProps: {
      handleKeyDown: (_view, event) => (handleKeyDownRef.current ? handleKeyDownRef.current(event) : false),
    },
    content: value || '<p></p>',
    editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      lastEmittedRef.current = html
      onChange(html)
    },
    onSelectionUpdate: () => force((v) => v + 1),
    onTransaction: () => force((v) => v + 1),
  })

  // Sync external value changes (e.g. when switching templates).
  useEffect(() => {
    if (!editor) return
    if (value === lastEmittedRef.current) return
    const current = editor.getHTML()
    if (value !== current) editor.commands.setContent(value || '<p></p>', false)
  }, [value, editor])

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable)
  }, [editable, editor])

  useEffect(() => {
    onEditorReady?.(editor ?? null)
  }, [editor, onEditorReady])

  useEffect(() => () => editor?.destroy(), [editor])

  const insertPlaceholder = useCallback(
    (key: string) => {
      if (!editor || !key) return
      editor.chain().focus().insertContent({ type: 'placeholder', attrs: { name: key } }).run()
    },
    [editor],
  )

  const setLink = useCallback(() => {
    if (!editor) return
    const previous = editor.getAttributes('link').href || ''
    const url = window.prompt('Enter URL (leave empty to remove):', previous)
    if (url === null) return
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }, [editor])

  const sortedPlaceholders = useMemo(() => [...placeholders].sort(), [placeholders])

  if (!editor) {
    return <div className={`border rounded ${className}`} style={{ minHeight: minHeight + 40 }} aria-busy="true" />
  }

  return (
    <div className={`rich-text-editor ${className}`}>
      {editable && (
        <Toolbar
          editor={editor}
          placeholders={sortedPlaceholders}
          onInsertPlaceholder={insertPlaceholder}
          onSetLink={setLink}
          variant={variant}
        />
      )}
      <EditorContent
        editor={editor}
        aria-label={ariaLabel}
        className={`rte-content border px-3 py-2 ${editable ? 'border-t-0 rounded-b' : 'rounded'} ${
          error ? 'border-red-400' : 'border-gray-200'
        }`}
        style={{ minHeight }}
      />
      {error && <p className="text-red-500 text-[11px] mt-1">{error}</p>}
    </div>
  )
}

const Toolbar = ({
  editor,
  placeholders,
  onInsertPlaceholder,
  onSetLink,
  variant = 'block',
}: {
  editor: Editor
  placeholders: string[]
  onInsertPlaceholder: (key: string) => void
  onSetLink: () => void
  variant?: 'block' | 'inline'
}) => {
  const currentSize = editor.getAttributes('textStyle').fontSize || ''
  const currentFamily = editor.getAttributes('textStyle').fontFamily || ''
  const currentColor = editor.getAttributes('textStyle').color || '#111827'

  return (
    <div className="rte-toolbar flex flex-wrap items-center gap-1 border border-b-0 rounded-t bg-gray-50 px-2 py-1">
      <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}>
        <ArrowUturnLeftIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}>
        <ArrowUturnRightIcon className="w-4 h-4" />
      </ToolbarButton>
      <Divider />

      <select
        title="Font"
        className="text-xs border border-gray-200 rounded px-1 py-1 bg-white"
        value={currentFamily}
        onChange={(e) => {
          const v = e.target.value
          if (!v) editor.chain().focus().unsetFontFamily().run()
          else editor.chain().focus().setFontFamily(v).run()
        }}
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f.value || 'default'} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        title="Size"
        className="text-xs border border-gray-200 rounded px-1 py-1 bg-white"
        value={currentSize}
        onChange={(e) => {
          const v = e.target.value
          if (!v) editor.chain().focus().unsetFontSize().run()
          else editor.chain().focus().setFontSize(v).run()
        }}
      >
        <option value="">Size</option>
        {FONT_SIZES.map((s) => (
          <option key={s} value={s}>
            {s.replace('px', '')}
          </option>
        ))}
      </select>

      <Divider />

      <ToolbarButton
        title="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <span className="line-through">S</span>
      </ToolbarButton>

      <label
        className="px-2 py-1 rounded text-sm border bg-white border-gray-200 hover:bg-gray-100 cursor-pointer flex items-center gap-1"
        title="Text color"
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="font-bold" style={{ color: currentColor }}>
          A
        </span>
        <input
          type="color"
          className="w-4 h-4 border-none p-0 bg-transparent cursor-pointer"
          value={currentColor}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />
      </label>

      <div className="flex items-center gap-0.5">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setColor(c).run()}
            title={c}
            className="w-4 h-4 border border-gray-200 rounded-sm"
            style={{ backgroundColor: c }}
          />
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().unsetColor().run()}
          title="Clear color"
          className="text-[11px] px-1 ml-1 border border-gray-200 rounded"
        >
          x
        </button>
      </div>

      {variant === 'block' && (
        <>
          <Divider />

          <ToolbarButton
            title="Align left"
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <Bars3BottomLeftIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Align center"
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <Bars3CenterLeftIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Align right"
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <Bars3BottomRightIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Justify"
            active={editor.isActive({ textAlign: 'justify' })}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <Bars3Icon className="w-4 h-4" />
          </ToolbarButton>
        </>
      )}

      <Divider />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListBulletIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        {/* Numbered list icon – inline SVG since heroicons 2.0.x doesn't include one */}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <path d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
        </svg>
      </ToolbarButton>
      {variant === 'block' && (
        <>
          <ToolbarButton
            title="Heading 1"
            active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            H1
          </ToolbarButton>
          <ToolbarButton
            title="Heading 2"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </ToolbarButton>
        </>
      )}

      <Divider />

      <ToolbarButton title="Insert / edit link" active={editor.isActive('link')} onClick={onSetLink}>
        Link
      </ToolbarButton>
      {variant === 'block' && (
        <ToolbarButton title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          ―
        </ToolbarButton>
      )}
      <ToolbarButton title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        Clear
      </ToolbarButton>

      {placeholders.length > 0 && (
        <>
          <Divider />
          <select
            title="Insert placeholder"
            className="text-xs border border-gray-200 rounded px-1 py-1 bg-white"
            value=""
            onChange={(e) => {
              const v = e.target.value
              if (!v) return
              onInsertPlaceholder(v)
              e.target.value = ''
            }}
          >
            <option value="">+ Placeholder</option>
            {placeholders.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  )
}

export default RichTextEditor
